// ===========================================
// Enroll Students Dialog Component
// ===========================================

import { getStudentAgeGroup, getStudentFamily } from '../../../api/users.api';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  InputAdornment,
  Alert,
  Typography,
  List,
  ListItem,
  FormControlLabel,
  Checkbox,
  Box,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { Student } from '../../../api/users.api';
import StartWeekSelect, { StartWeekLesson, StartWeekTerm } from './StartWeekSelect';

interface EnrollStudentsDialogProps {
  open: boolean;
  onClose: () => void;
  students: Student[];
  selectedStudents: string[];
  studentSearch: string;
  onSearchChange: (search: string) => void;
  onToggleStudent: (studentId: string) => void;
  onEnroll: () => void;
  isLoading: boolean;
  availableSpots?: number;
  /**
   * Mid-term start: when `lesson`, `term` and `onStartWeekChange` are all given, a "Starting week"
   * select is shown. `startWeek` null = the class's own start (nothing is sent). One start week
   * covers the whole batch.
   */
  lesson?: StartWeekLesson;
  term?: StartWeekTerm & { name: string };
  startWeek?: number | null;
  onStartWeekChange?: (startWeek: number | null) => void;
}

export default function EnrollStudentsDialog({
  open,
  onClose,
  students,
  selectedStudents,
  studentSearch,
  onSearchChange,
  onToggleStudent,
  onEnroll,
  isLoading,
  availableSpots,
  lesson,
  term,
  startWeek = null,
  onStartWeekChange,
}: EnrollStudentsDialogProps) {
  // Filter students by search
  const filteredStudents = students.filter((student) => {
    const searchLower = studentSearch.toLowerCase();
    return (
      student.firstName.toLowerCase().includes(searchLower) ||
      student.lastName.toLowerCase().includes(searchLower)
    );
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Enrol Students</DialogTitle>
      <DialogContent>
        <TextField
          placeholder="Search students..."
          value={studentSearch}
          onChange={(e) => onSearchChange(e.target.value)}
          fullWidth
          size="small"
          sx={{ mt: 1, mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        {lesson && term && onStartWeekChange && (
          <Box sx={{ mb: 2 }}>
            <StartWeekSelect
              lesson={lesson}
              term={term}
              value={startWeek}
              onChange={onStartWeekChange}
              disabled={isLoading}
              helperText={`Applies to ${term.name} only.`}
            />
          </Box>
        )}

        {availableSpots !== undefined && availableSpots < selectedStudents.length && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            You've selected more students than available spots ({availableSpots}).
          </Alert>
        )}

        {filteredStudents.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            {studentSearch
              ? 'No students match your search.'
              : 'All eligible students are already enrolled.'}
          </Typography>
        ) : (
          <List sx={{ maxHeight: 300, overflow: 'auto' }}>
            {filteredStudents.map((student: Student) => (
              <ListItem key={student.id} dense>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedStudents.includes(student.id)}
                      onChange={() => onToggleStudent(student.id)}
                    />
                  }
                  label={
                    <Box>
                      <Typography>
                        {student.firstName} {student.lastName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {getStudentAgeGroup(student)?.name || '—'}
                        {getStudentFamily(student)?.name && ` - ${getStudentFamily(student)?.name}`}
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onEnroll}
          disabled={selectedStudents.length === 0 || isLoading}
        >
          {isLoading
            ? 'Enrolling...'
            : `Enrol ${selectedStudents.length} Student${selectedStudents.length !== 1 ? 's' : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
